import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import PropertyCard from "@/components/landlord/PropertyCard";
import AddPropertyDialog, { INITIAL_FORM } from "@/components/landlord/AddPropertyDialog";
import AssignAmcDialog from "@/components/landlord/AssignAmcDialog";
import AssignTenantDialog from "@/components/landlord/AssignTenantDialog";

export default function LandlordDashboard() {
  const { t } = useTranslation();

  const [properties, setProperties] = useState([]);
  const [amcs, setAmcs] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);

  const [amcOpen, setAmcOpen] = useState(false);
  const [amcTarget, setAmcTarget] = useState(null);
  const [amcEmail, setAmcEmail] = useState("");

  const [tenantOpen, setTenantOpen] = useState(false);
  const [tenantTarget, setTenantTarget] = useState(null);
  const [tenantEmail, setTenantEmail] = useState("");

  const load = useCallback(async () => {
    const [p, a] = await Promise.all([api.get("/properties/mine"), api.get("/amcs")]);
    setProperties(p.data);
    setAmcs(a.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Apply calculator scenario when arriving via ?prefill=1
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("prefill") !== "1") return;
    try {
      const raw = sessionStorage.getItem("lm_scenario");
      if (!raw) return;
      const s = JSON.parse(raw);
      const monthlyRent = Number(s.annualNet) / 12;
      setForm((f) => ({
        ...f,
        name: "Facility from calculator",
        address: "—",
        units: [{
          name: "Unit 1", monthly_rent: Number(monthlyRent.toFixed(2)),
          tenant_name: "", lease_end: "",
        }],
      }));
      setCreateOpen(true);
      toast.success(t("scenario_loaded"));
    } catch (err) { console.warn("Scenario prefill failed:", err); }
    finally {
      sessionStorage.removeItem("lm_scenario");
      const next = new URLSearchParams(searchParams);
      next.delete("prefill");
      setSearchParams(next, { replace: true });
    }
  }, []);

  const createProp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/properties", {
        ...form,
        units: form.units.map((u) => ({ ...u, monthly_rent: Number(u.monthly_rent) })),
      });
      toast.success("✓");
      setCreateOpen(false);
      setForm(INITIAL_FORM);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submitAmc = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${amcTarget.id}/assign-amc`, { amc_email: amcEmail });
      toast.success("✓"); setAmcOpen(false); setAmcEmail(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submitTenant = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/properties/${tenantTarget.id}/assign-tenant`, { tenant_email: tenantEmail });
      toast.success("✓"); setTenantOpen(false); setTenantEmail(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openAmc = (p) => { setAmcTarget(p); setAmcOpen(true); };
  const openTenant = (p) => { setTenantTarget(p); setTenantOpen(true); };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12">
      <div className="flex justify-between items-end mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono-label text-[11px] text-[#002FA7]">⎯⎯ {t("ll_tag")}</div>
          <h1 className="font-display text-5xl font-black mt-2">{t("ll_title")}</h1>
        </div>
        <AddPropertyDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          form={form}
          setForm={setForm}
          onSubmit={createProp}
        />
      </div>

      {properties.length === 0 ? (
        <div className="border border-black/10 p-16 text-center">
          <div className="font-mono-label text-[11px] text-zinc-500">{t("ll_empty_tag")}</div>
          <div className="font-display text-2xl font-bold mt-3">{t("ll_empty_title")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {properties.map((p) => (
            <PropertyCard
              key={p.id}
              property={p}
              onAssignAmc={openAmc}
              onAssignTenant={openTenant}
            />
          ))}
        </div>
      )}

      <AssignAmcDialog
        open={amcOpen}
        onOpenChange={setAmcOpen}
        target={amcTarget}
        amcs={amcs}
        amcEmail={amcEmail}
        setAmcEmail={setAmcEmail}
        onSubmit={submitAmc}
      />
      <AssignTenantDialog
        open={tenantOpen}
        onOpenChange={setTenantOpen}
        target={tenantTarget}
        tenantEmail={tenantEmail}
        setTenantEmail={setTenantEmail}
        onSubmit={submitTenant}
      />
    </div>
  );
}
